import type { V2AiObservation } from '../domain/aiObservation';
import { tradeForResult } from '../domain/inspectionRules';
import { AI_GUARDED_TRADES, RESPONSIBLE_TRADE_ORDER, canonicalResponsibleTrade, type ResponsibleTrade } from '../domain/responsibleTrade';
import { translate, type MessageKey, type MessageParams } from '../i18n/i18n';
import { tradeLabel } from '../i18n/labels';
import { containsChinese } from '../report/boundText';
import { referralSentence } from '../report/rowWording';

// 记为其他问题 on an unclassified AI observation (CA-5): the other-finding editor opens pre-filled with the AI's
// text and photos; what the inspector saves is the inspector's finding. 2026-10-28 (iPhone real-photo run
// 2026-10-26 #8, device walkthrough 2026-10-27 A5):
//   - LANGUAGE. An other finding prints as written in the English client report, so its fields are drafted in
//     English. The AI writes `observation` / `recommendation` in the report language of the analysis (English
//     unless the report language had been switched to Chinese) and repeats them in the App Language as
//     `observationUi` / `recommendationUi`; the draft used to take the App-Language copy, so with the app in
//     Chinese every AI-drafted other finding was Chinese and the closeout then blocked the English report.
//     Now each field takes the copy that is free of Chinese, and only falls back to the other copy when the
//     analysis carried no English at all. The AI's `title` (and `concern` / `guidance`) exist in the App
//     Language only — the server returns no English title, so a Chinese title stays and the editor says
//     under the field that the English report prints it (FindingEditor).
//   - ONE PROFESSIONAL. The recommendation sentence and the trade setting name the same professional: the
//     trade is the rules library's (src/domain/inspectionRules.ts tradeForResult — re-read here, so an
//     observation stored before a rule was corrected drafts with the corrected trade), unless that is only the
//     generic contractor (or none) and the AI's recommendation names a specific one, which is then taken; the
//     recommendation is the AI's English sentence when it already names that professional, otherwise the
//     report's own referral sentence for it ("Evaluation and repair by a Qualified Siding Contractor is
//     recommended.").
export interface AiOtherFindingDraft {
  title: string;
  observation: string;
  recommendation: string;
  responsibleTrade: ResponsibleTrade | null;
  evidenceIds: string[];
}

const tEn = (key: MessageKey, params?: MessageParams) => translate('en-US', key, params);
const tZh = (key: MessageKey, params?: MessageParams) => translate('zh-CN', key, params);

function firstEnglish(...texts: ReadonlyArray<string | null | undefined>): string | null {
  for (const text of texts) {
    const value = (text ?? '').trim();
    if (value.length > 0 && !containsChinese(value)) return value;
  }
  return null;
}

// The words a recommendation names a professional by: each label's parts without the adjective, and the
// common names the model uses. The generic contractor, "Other" and the two AI-guarded trades never count.
const EXTRA_NAMES: ReadonlyArray<[string, ResponsibleTrade]> = [
  ['plumber', 'licensedPlumbingProfessional'],
  ['水管工', 'licensedPlumbingProfessional'],
  ['roofer', 'qualifiedRoofingContractor'],
  ['屋面承包商', 'qualifiedRoofingContractor'],
  ['hvac', 'qualifiedHvacContractor'],
  ['oil burner', 'qualifiedHvacContractor'],
  ['heating contractor', 'qualifiedHvacContractor'],
  ['暖通', 'qualifiedHvacContractor'],
  ['exterminator', 'licensedPestProfessional'],
  ['siding', 'qualifiedSidingContractor']
];

function namesOf(trade: ResponsibleTrade): string[] {
  return [tradeLabel(tEn, trade), tradeLabel(tZh, trade)]
    .flatMap((label) => label.split(/\s*\/\s*/))
    .map((part) => part.replace(/^(qualified|licensed)\s+/i, '').trim().toLowerCase())
    .filter((part) => part.length >= 2);
}

const NAMED_TRADES: ReadonlyArray<[string, ResponsibleTrade]> = [
  ...RESPONSIBLE_TRADE_ORDER.filter((trade) => trade !== 'other' && trade !== 'qualifiedGeneralContractor' && !AI_GUARDED_TRADES.includes(trade)).flatMap((trade) => namesOf(trade).map((name) => [name, trade] as [string, ResponsibleTrade])),
  ...EXTRA_NAMES
].sort((a, b) => b[0].length - a[0].length);

export function tradeNamedIn(...texts: ReadonlyArray<string | null | undefined>): ResponsibleTrade | null {
  const haystack = texts.map((t) => (t ?? '').toLowerCase()).join('\n');
  for (const [name, trade] of NAMED_TRADES) {
    const english = /^[a-z][a-z\s&()-]*$/.test(name);
    if (english ? new RegExp(`\\b${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`).test(haystack) : haystack.includes(name)) return trade;
  }
  return null;
}

function mentions(text: string, trade: ResponsibleTrade): boolean {
  return tradeNamedIn(text) === trade;
}

// The English referral sentence for a trade ('' for none / Other, which carries its own words).
export function referralFor(trade: ResponsibleTrade | null): string {
  if (trade === null || trade === 'other') return '';
  return referralSentence('en-US', tradeLabel(tEn, trade));
}

// The trade setting moved: a recommendation that is still exactly the referral of the old trade follows it,
// so the sentence and the setting never name two professionals. Anything the inspector wrote is kept.
export function recommendationAfterTradeChange(recommendation: string, from: ResponsibleTrade | null, to: ResponsibleTrade | null): string {
  const before = referralFor(from);
  if (before.length === 0 || recommendation.trim() !== before) return recommendation;
  const after = referralFor(to);
  return after.length > 0 ? after : recommendation;
}

export function otherFindingDraftFrom(o: V2AiObservation): AiOtherFindingDraft {
  const englishObservation = firstEnglish(o.observation, o.observationUi);
  const observation = englishObservation
    ? [englishObservation, firstEnglish(o.concern) ?? ''].filter((x) => x.length > 0).join('\n')
    : [o.observationUi ?? o.observation, o.concern].map((x) => x.trim()).filter((x) => x.length > 0).join('\n');
  const englishRecommendation = firstEnglish(o.recommendation, o.recommendationUi);
  const ruled = canonicalResponsibleTrade(tradeForResult(o, o.scope?.subjectId ?? null) ?? o.responsibleTrade);
  const named = tradeNamedIn(o.recommendation, o.recommendationUi);
  const trade = (ruled === null || ruled === 'qualifiedGeneralContractor') && named !== null ? named : ruled;
  let recommendation: string;
  if (trade !== null && trade !== 'other') recommendation = englishRecommendation && mentions(englishRecommendation, trade) ? englishRecommendation : referralFor(trade);
  else recommendation = englishRecommendation ?? [o.recommendationUi ?? o.recommendation, o.guidance].map((x) => x.trim()).filter((x) => x.length > 0).join('\n');
  return { title: o.title.trim(), observation, recommendation, responsibleTrade: trade, evidenceIds: [...o.photoIds] };
}
